import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSlate } from 'slate-react'
import { useFormContext, useFieldArray, Controller } from 'react-hook-form'
import clsx from 'clsx';
import { toDate } from 'date-fns';
import {Media, ButtonGroup, Card, Button, Input,FormGroup, InputGroup,InputGroupAddon,ListGroup, ListGroupItem} from 'reactstrap';

import { useEditorState, useEditorActions, useEditor, UPDATE_VARIABLE } from '../../editor-context'
import { removeNodesById, scrollToNode, getNodesById, getNodesByType } from '../../Canvas/TratoFormat'
import { format, noop } from 'utils'
import useDebounce from 'hooks/useDebounce'
import useMemoCompare from 'hooks/useMemoCompare'
import UploadAndAnalyse from 'components/UploadAndAnalyse/UploadAndAnalyse'
import DatePicker from 'components/Shared/DatePicker'
import { useSearchParticipantVariables } from './VariableList';
import { useProfileAbility } from 'context/profile'

export function Preview() {
    const { t } = useTranslation()
    const { document: doc, participants } = useEditorState()
    const profileAbility = useProfileAbility();
    const { updateDocument } = useEditorActions();
    const editor = useSlate();
    
    const [isUploadAndAnalyseOpen, setIsUploadAndAnalyseOpen] = useState(false);

    const canViewExtractInfo = profileAbility.can('VIEW_EXTRACT_INFO'); // can VIEW_EXTRACT_INFO
    const canApplyWordVariables = profileAbility.can('APPLY_VARIABLES_WORD_ONLINE'); // can VIEW_EXTRACT_INFO
    const { wordonline } = doc;

    const variablesInDocument = useMemo(() => {
        return doc.participants.map(participant => {
            const variablesInDocument = [];
            return {
                ...participant,
                variables: [...getNodesByType(editor, 'variable', { 
                    match: (n) => n.participantId === participant._id && n.variableType !== 'constant'
                })]?.filter(([n]) => { 
                    if (!variablesInDocument.includes(n._id)) {
                        variablesInDocument.push(n._id) 
                        return true;
                    }
                    
                    return false;
                })?.map(([variable]) => {
                    return participants?.get(participant._id)?.variables?.get(variable._id);
                }).filter(v => v) ?? []
            }
        })
    }, [editor, participants, doc.participants])
    
    const { filteredList, search, onSearchChange } = useSearchParticipantVariables(variablesInDocument);

    const handleLoadDocument = () => {
        setIsUploadAndAnalyseOpen(true);
    }
    const handleClose = () => {
        setIsUploadAndAnalyseOpen(false);
    }

    const handleUploadAndAnalyzeSuccess = (data) => {
        if (data.contract) {
            updateDocument(data.contract, { updateElements: false })
        }
    }

    return (
        <Card className="bg-light text-dark rounded-0">
            <Media>
                <Media body className="p-2">
                    <Media body>
                            <InputGroup inline="true">
                            {wordonline &&
                            <InputGroupAddon addonType="prepend">
                                <Button
                                    disabled={!canApplyWordVariables}
                                    title={canApplyWordVariables ? t('Aplicar las variables al documento') : t('wordList.plans.availablePE')}
                                    data-action="read"
                                    color="success"
                                >
                                    <i className="fas fa-file-word"></i>
                                </Button>
                                </InputGroupAddon>
                            } 
                            <Input
                                type="text"
                                placeholder={t('editor.variables.search')}
                                value={search}
                                onChange={onSearchChange}
                            />
                            </InputGroup>
                    </Media>
                    {canViewExtractInfo &&
                        <Media body className="mt-2">
                            <Button color="primary" size="sm" block onClick={handleLoadDocument}>
                                <i className="fas fa-file"></i>
                                <span>{' '}{t('editor.variables.extract_info')}</span>
                            </Button>
                            <div className="mt-2">
                            {doc.files.map(file => (
                                <p>{file.name}</p>
                            ))}
                            {
                                !doc.files.length &&
                                <p>{t('editor.variables.no_file')}</p>
                            }
                            </div>
                        </Media>
                    }
                </Media>
            </Media>
                {filteredList?.map((participant, i) => {
                    return <ParticipantVariables
                        key={participant._id}
                        documentLanguage={doc.language?.toLowerCase?.()}
                        participant={participant}
                        name={`participants[${i}]`}
                        index={i + 1}
                    />
                })}
                <UploadAndAnalyse contract={doc} isOpen={isUploadAndAnalyseOpen} onClose={handleClose} onSuccess={handleUploadAndAnalyzeSuccess} />
        </Card>
    )
}


function ParticipantVariables({  name, participant, documentLanguage, index }) {
    const { t } = useTranslation()
    const { updateVariable } = useEditorActions();
    const [{ participants }, dispatch] = useEditor();
    const editor = useSlate();
    const memoizedParticipant = useMemoCompare(participant, (prev, next) => {
        return prev && prev._id === next._id;
    })

    
    

    const handleSave = useCallback((variable) => {
        updateVariable({
            participantId: participant._id,
            variable,
        })
    }, [participant, updateVariable])

    const handleChange = (variable) => {
        // dispatch({ type: UPDATE_VARIABLE, participantId: participant._id, variable })
    }

    return (
        <ListGroup className="bg-light">
            {participant.variables?.map(variable => (
                <VariableItem participantId={participant._id} key={variable._id} documentLanguage={documentLanguage} variable={variable} participantIndex={index} onSave={handleSave} onChange={handleChange} />
            ))}
        </ListGroup>
    )
}

const isDateVariable = (v) => {
    return v.type === 'date'
}

function VariableItem({ variable, participantId, documentLanguage, participantIndex, onSave, onChange }) {
    const { t, i18n } = useTranslation()
    const editor = useSlate();
    const name = `previewVariables[${participantId}][${variable._id}]`

    const { register, watch, control, setValue, handleSubmit } = useFormContext({
        mode: 'onSubmit',
        defaultValues: {
           [`${name}.value`]: variable.value,
           [`${name}.dateValue`]: isDateVariable(variable) ? variable.dateValue : null,
        }
    });
    
    useEffect(() => {
        if (variable.type === 'date' && variable.dateValue) {
            setValue(`${name}.dateValue`, variable.dateValue)
        } else if (variable.value) {
            setValue(`${name}.value`, variable.value)
        }
    }, [variable.type, variable.dateValue, variable.value])

    const handleClick = () => {
        scrollToNode(editor, variable)
    }

    const handleSave = () => {
        handleSubmit((data) => { 
            const parsedData = data.previewVariables[participantId][variable._id];

            onSave({ ...variable, dateValue: parsedData.dateValue, value:  isDateVariable(variable) ? format(parsedData.dateValue, 'PPP') : parsedData.value }) 
        })()
    }

    if (!variable) {
        return null;
    }

    // if (isDateVariable(variable)) console.log({variable})

    return (
        <ListGroupItem className="bg-light border-0 px-3 py-2">
            <FormGroup>
            <label>
                <i className={`fas fa-circle participant${participantIndex}-color`}></i> {variable.name}
            </label>
            <input type="hidden" name={`${name}.variableId`} ref={register} defaultValue={variable._id} />
            <div onClick={handleClick}>
                <VariableInput 
                    prefix={name}
                    variable={variable}
                    control={control}
                    documentLanguage={documentLanguage}
                    onBlur={handleSave}
                />
            </div>
            </FormGroup>
        </ListGroupItem>
    )
}

export function VariableInput({ prefix, documentLanguage, variable = {}, control, maxLength, onChange: onInputChange = noop, onBlur: onInputBlur = noop, onCalendarOpen= noop, onCalendarClose = noop }) { 
    const { t }= useTranslation();
    const name = prefix ? `${prefix}.` : '';

    if (variable.type === 'fixed') {
        return (
            <Controller
                control={control}
                name={`${name}value`}
                defaultValue={variable.value}
                render={({ onChange, onBlur, value }) => (
                    <select
                        className="form-control input-sm"
                        onChange={(evt) => {
                            onChange(evt.target.value)
                            onInputChange(evt)
                        }}
                        onBlur={(evt) => {
                            onInputBlur(evt)
                            onBlur(evt)
                        }}
                        value={value}
                    >
                        <option value="">{t('editor.variables.value_placeholder')}</option>
                        {variable.options?.map((option, index) => (
                            <option key={index} value={option?.value ?? option}>{option?.value ?? option}</option>
                        ))}
                    </select>

                )}
            />
        )
    }

    if (variable.type === 'textarea') {
        return (
            <Controller
                control={control}
                name={`${name}value`}
                defaultValue={variable.value}
                render={({ onChange, onBlur, value }) => (
                    <textarea
                        className="form-control"
                        value={value}
                        onChange={(evt) => {
                            onChange(evt.target.value)
                            onInputChange(evt)
                        }}
                        onBlur={(evt) => {
                            onBlur(evt)
                            onInputBlur(evt)
                        }}
                    />
                )}
            />
        )
    }

    if (variable.type === 'date') {
        return (
            <Controller
                control={control}
                name={`${name}dateValue`}
                defaultValue={variable.dateValue || null}
                render={({ onChange, onBlur, value }) => (
                    <DatePicker
                        className="w-100"
                        locale={documentLanguage}
                        selected={value ? new Date(value) : null}
                        onChange={(date) => {
                            onChange(date)
                            onInputChange(date)
                            onInputBlur(date)
                        }}
                        onBlur={(evt) => {
                            onBlur(evt)
                            onInputBlur(evt)
                        }}
                        onCalendarOpen={onCalendarOpen}
                        onCalendarClose={onCalendarClose}
                        customInput={<input className="form-control input-sm" type="text" />}
                        dateFormat="PPP"
                        // placeholderText={t('editor.config.expiry_start_placeholder')}
                    />
                )}
            />
        )
    }

    return (
        <Controller
            control={control}
            name={`${name}value`}
            defaultValue={variable.type === 'dynamictable' ? t('editor.variables.type_dynamictable') : variable.value}
            render={({ onChange, onBlur, value }) => (
                <input
                    type="text"
                    className="form-control input-sm"
                    // placeholder={t('editor.variables.value_placeholder')}
                    name="value"
                    value={variable.type === 'dynamictable' ? t('editor.variables.type_dynamictable') : value}
                    disabled={['dynamictable', 'ocr:ine'].includes(variable.type)}
                    maxLength={variable.type === 'text' && variable.limit ? variable.limit : 10000}
                    size={variable.type === 'text' && variable.limit ? variable.limit : 10000}
                    onChange={(evt) => {
                        onChange(evt.target.value)
                        onInputChange(evt)
                    }}
                    onBlur={(evt) => {
                        onBlur(evt)
                        onInputBlur(evt)
                    }}
                />
            )}
        />
    )
}